// Talks to the Worker. Replaces the Claude artifact's window.storage, which saved each collection
// as one shared blob (last writer wins). Here the app still hands over a whole updated array, but
// only the records that actually changed are sent, each with the version it was based on.

export class ApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.status = status;
    this.body = body || {};
  }
}

// Which office this tab is working in. It arrives as ?office=<id> on the link from the office's
// Tools page and is remembered for next time; the account service decides whether you belong to it.
export function currentOffice() {
  let office = "";
  try {
    office = new URLSearchParams(location.search).get("office") || "";
    if (office) localStorage.setItem("ett-office", office);
    else office = localStorage.getItem("ett-office") || "";
  } catch {}
  return office.toLowerCase();
}
const OFFICE = typeof location !== "undefined" ? currentOffice() : "";

async function call(method, path, body) {
  const headers = { "X-Office": OFFICE };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  let data = {};
  try {
    data = await res.json();
  } catch {
    // A non-JSON reply (a proxy error page, a dropped connection) still gets a readable message below.
  }
  if (!res.ok) throw new ApiError(data.error || `The server replied ${res.status}`, res.status, data);
  return data;
}

export const getSession = () => call("GET", "/api/session");
export const fetchData = () => call("GET", "/api/data");
export const devLogin = (details) => call("POST", "/api/dev-login", details);
export const devLogout = () => call("POST", "/api/dev-logout", {});
export const markBackupTaken = () => call("POST", "/api/backup-taken", {});
// 2.1.3: backupOffice is the office the backup says it came from (null for older files); the server
// refuses another office's backup, or one that doesn't say, unless confirmOffice is true.
export const restoreBackup = (collections, { backupOffice = null, confirmOffice = false } = {}) =>
  call("POST", "/api/restore", { collections, backupOffice, confirmOffice });

// Key order doesn't count as a change: a record rebuilt with the same values in a different order
// must not be re-sent (and re-versioned) on every save.
function stable(v) {
  if (Array.isArray(v)) return `[${v.map(stable).join(",")}]`;
  if (v && typeof v === "object") return `{${Object.keys(v).sort().filter((k) => v[k] !== undefined).map((k) => `${JSON.stringify(k)}:${stable(v[k])}`).join(",")}}`;
  return JSON.stringify(v === undefined ? null : v);
}
const same = (a, b) => a === b || stable(a) === stable(b);

// Keeps the last copy of each collection the server confirmed, and turns "here is the new array"
// into a minimal list of upserts and deletes. Saves run one at a time, in order, so a quick second
// edit always builds on the version the first one produced.
export class Syncer {
  constructor() {
    this.synced = { elections: [], tasks: [], people: [] };
    this.versions = {};
    this.queue = Promise.resolve();
    this.pending = 0;
  }

  async load() {
    const data = await call("GET", "/api/data");
    this.synced = { elections: data.elections, tasks: data.tasks, people: data.people };
    this.versions = { ...data.versions };
    return data;
  }

  diff(collection, next) {
    const before = new Map(this.synced[collection].map((r) => [r.id, r]));
    const after = new Map(next.map((r) => [r.id, r]));
    const changes = [];
    for (const [id, rec] of after) {
      if (!before.has(id) || !same(before.get(id), rec)) {
        changes.push({ collection, op: "upsert", id, baseVersion: this.versions[`${collection}/${id}`] || 0, data: rec });
      }
    }
    for (const id of before.keys()) {
      if (!after.has(id)) changes.push({ collection, op: "delete", id, baseVersion: this.versions[`${collection}/${id}`] || 0 });
    }
    return changes;
  }

  // updates: { tasks: nextTasks, people: nextPeople, ... } saved together, all-or-nothing.
  save(updates) {
    this.pending += 1;
    const run = async () => {
      const changes = Object.entries(updates).flatMap(([c, next]) => this.diff(c, next));
      if (changes.length === 0) return;
      const res = await call("POST", "/api/changes", { changes });
      for (const [key, v] of Object.entries(res.versions || {})) {
        if (v === 0) delete this.versions[key];
        else this.versions[key] = v;
      }
      for (const [c, next] of Object.entries(updates)) this.synced[c] = next;
    };
    const p = this.queue.then(run);
    // A failed save must not wedge every later one behind it.
    this.queue = p.catch(() => {}).finally(() => { this.pending -= 1; });
    return p;
  }

  idle() {
    return this.pending === 0;
  }
}
